import {
  lazy, Suspense, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TDefaultDrawerProps } from '../../types/components';
import ChestStarBg from '../../assets/svg/backgrounds/tutorial-menu-star.svg';
import SpinStarBg from '../../assets/svg/backgrounds/tutorial-spin-star.svg';
import BoosterStarBg from '../../assets/svg/backgrounds/tutorial-booster-star.svg';
import getBoosterIcon from '../../Components/utils/getBoosterIcon';
import { useAppDispatch, useAppSelector } from '../../store';
import { TChestReward, TChestRewardHistory } from '../../types';
import { cancelQuest, getQuests } from '../../store/reducers/quests/asyncActions';
import { claimChestReward, getActiveChests } from '../../store/reducers/chests/asyncActions';
import { getMe } from '../../store/reducers/profile/asyncActions';

const TutorialChestStep = lazy(() => import('./TutorialChestStep'));
const TutorialEarnStep = lazy(() => import('./TutorialEarnStep'));
const TutorialUpgradeStep = lazy(() => import('./TutorialUpgradeStep'));
const TutorialStoreStep = lazy(() => import('./TutorialStoreStep'));
const TutorialSpinStep = lazy(() => import('./TutorialSpinStep'));
const TutorialBoosterStep = lazy(() => import('./TutorialBoosterStep'));
const TutorialEnergyStep = lazy(() => import('./TutorialEnergyStep'));
const TutorialMultiplyStep = lazy(() => import('./TutorialMultiplyStep'));
const TutorialReceiveChestStep = lazy(() => import('./TutorialReceiveChestStep'));
const TutorialJoinTelegramStep = lazy(() => import('./TutorialJoinTelegramStep'));
const TutorialConfirmSubscriptionStep = lazy(() => import('./TutorialConfirmSubscriptionStep'));
const TutorialFailedTaskStep = lazy(() => import('./TutorialFailedTaskStep'));
const TutorialSkipStep = lazy(() => import('./TutorialSkipStep'));
const TutorialFinalStep = lazy(() => import('./TutorialFinalStep'));
const TutorialWelcomeStep = lazy(() => import('./TutorialWelcomeStep'));

function TutorialModal({ open }: TDefaultDrawerProps) {
  const dispatch = useAppDispatch();
  const {
    chestsHistory,
  } = useAppSelector(({ chests }) => chests);
  const { questsList } = useAppSelector(({ quests }) => quests);
  const tutorialQuest = questsList?.tutorial[0] || null;
  const tutorialChest = chestsHistory?.find((cH) => cH.obtained_by === 'tutorial');
  const initialStep = () => {
    if (questsList?.tutorial[0]?.progress?.status === 'started') {
      return 12;
    }
    if (questsList?.tutorial?.[0].progress?.status === 'done' && tutorialChest?.status !== 'claimed') {
      return 15;
    }
    if (tutorialChest?.status === 'opened') {
      return 11;
    }

    return 1;
  };
  const [screen, setScreen] = useState(initialStep());
  const [chestReward, setChestReward] = useState<TChestReward | null>(null);

  useEffect(() => {
    if (chestsHistory?.[0]?.chest_rewards) {
      setChestReward(chestsHistory?.[0]?.chest_rewards);
    }
  }, [chestsHistory]);
  const getScreenHighlights = () => {
    switch (screen) {
      case 1:
        return null;
      case 2:
        return {
          originalID: 'menu-Chests',
          bg: ChestStarBg,
          starScale: 60,
          isMenu: true,
        };
      case 3:
        return {
          originalID: 'menu-Earn',
          bg: ChestStarBg,
          starScale: 60,
          isMenu: true,
        };
      case 4:
        return {
          originalID: 'menu-Upgrade',
          bg: ChestStarBg,
          starScale: 60,
          isMenu: true,
        };
      case 5:
        return {
          originalID: 'menu-Store',
          bg: ChestStarBg,
          starScale: 60,
          isMenu: true,
        };
      case 6:
        return {
          originalID: 'spin-button',
          bg: SpinStarBg,
          starScale: 120,
          isMenu: false,
          isSpin: true,
        };
      case 7:
        return {
          originalID: null,
          bg: ChestStarBg,
          starScale: 120,
          isMenu: false,
          isSpin: false,
          customElement: <CustomElement />,
        };
      case 8:
        return {
          originalID: 'spins-screen',
          bg: SpinStarBg,
          starScale: 120,
          isMenu: false,
          isSpin: false,
        };
      case 9:
        return {
          originalID: 'slot-multiply-button',
          bg: SpinStarBg,
          starScale: 120,
          isMultiply: true,
        };
      default:
        return null;
    }
  };
  useEffect(() => {
    const highlightedObjectOptions = getScreenHighlights();
    const bgElem = document.createElement('img') as HTMLImageElement;
    let clonedElement: HTMLElement | null = null;
    let originalElement: HTMLElement | null = null;

    if (highlightedObjectOptions?.originalID) {
      originalElement = document.getElementById(highlightedObjectOptions.originalID);
      if (originalElement) {
        if (highlightedObjectOptions.isMenu) {
          originalElement.children[0].classList.add('active-menu-item-custom');
        }
        clonedElement = originalElement.cloneNode(true) as HTMLElement;
        if (clonedElement) {
          if (highlightedObjectOptions.isSpin) {
            clonedElement.children[0].children[4].setAttribute('x', '30%');
          }
          if (highlightedObjectOptions.isMultiply) {
            try {
              clonedElement?.children[0]?.children[0]?.children[1].children[0].children[1].children[0].children[2].children[0].setAttribute('x', '40%');
            } catch (e) {
              window.console.log(e);
            }
          }
          document.body.appendChild(bgElem);
          document.body.appendChild(clonedElement);
          const originalElementRect = originalElement.getBoundingClientRect();
          clonedElement.style.position = 'absolute';
          clonedElement.style.transform = 'unset';
          clonedElement.style.listStyle = 'none';
          clonedElement.style.top = `${originalElementRect.top}px`;
          clonedElement.style.left = `${originalElementRect.x}px`;
          clonedElement.style.width = `${originalElementRect.width}px`;
          clonedElement.style.height = `${originalElementRect.height}px`;
          clonedElement.style.zIndex = '9999';
          clonedElement.style.pointerEvents = 'none';
          const bgScaleSize = highlightedObjectOptions.starScale;
          bgElem.src = highlightedObjectOptions.bg;
          bgElem.style.position = 'absolute';
          bgElem.style.transform = 'unset';
          bgElem.style.listStyle = 'none';
          bgElem.style.pointerEvents = 'none';
          bgElem.style.top = `${originalElementRect.top - (bgScaleSize / 2)}px`;
          bgElem.style.left = `${originalElementRect.x - (bgScaleSize / 2)}px`;
          bgElem.style.width = `${originalElementRect.width + bgScaleSize}px`;
          bgElem.style.height = `${originalElementRect.height + bgScaleSize}px`;
          bgElem.style.zIndex = '9999';
        }
      }
    }
    return () => {
      bgElem.remove();
      clonedElement?.remove();
      originalElement?.children[0]?.classList?.remove('active-menu-item-custom');
    };
  }, [screen]);

  const handleNext = (): void => {
    setScreen((prev) => Math.min(prev + 1, 15));
  };
  const handleValidateTGJoinSuccess = (): void => {
    setScreen(15);
  };
  const handleValidateTGJoinError = (): void => {
    setScreen(13);
  };

  const handleBackToJoin = (): void => {
    setScreen(11);
  };
  const handleFinishTutorial = async () => {
    if (tutorialChest?.id) {
      await dispatch(claimChestReward({ id: tutorialChest.id }));
      await dispatch(getMe());
      await dispatch(getActiveChests());
    }
  };
  const handleCancelQuest = async () => {
    if (tutorialQuest) {
      await dispatch(cancelQuest({ id: tutorialQuest?.id }));
      await dispatch(getQuests());
    }
  };
  const handleOpenChestStepComplete = (reward: TChestRewardHistory) => {
    getActiveChests();
    setChestReward(reward);
    handleNext();
  };
  const screens = useMemo(() => [
    <TutorialWelcomeStep key="step-1" onNext={handleNext} />,
    <TutorialChestStep key="step-2" onNext={handleNext} />,
    <TutorialEarnStep key="step-3" onNext={handleNext} />,
    <TutorialUpgradeStep key="step-4" onNext={handleNext} />,
    <TutorialStoreStep key="step-5" onNext={handleNext} />,
    <TutorialSpinStep key="step-6" onNext={handleNext} />,
    <TutorialBoosterStep key="step-7" onNext={handleNext} />,
    <TutorialEnergyStep key="step-8" onNext={handleNext} />,
    <TutorialMultiplyStep key="step-9" onNext={handleNext} />,
    <TutorialReceiveChestStep key="step-10" onNext={handleOpenChestStepComplete} />,
    <TutorialJoinTelegramStep key="step-11" onNext={handleNext} reward={chestReward} />,
    <TutorialConfirmSubscriptionStep key="step-12" onValidateSuccess={handleValidateTGJoinSuccess} onValidateError={handleValidateTGJoinError} tutorialQuest={tutorialQuest} />,
    <TutorialFailedTaskStep key="step-13" onTryAgain={handleBackToJoin} onExit={handleNext} />,
    <TutorialSkipStep key="step-14" onBackToJoin={handleBackToJoin} onCancelQuest={handleCancelQuest} />,
    <TutorialFinalStep key="step-15" onNext={handleFinishTutorial} reward={chestReward} />,
  ], [chestReward, tutorialQuest]);

  const getModalPosition = () => {
    const spinButton = document.getElementById('spin-button');
    const spinInfo = document.getElementById('spins-screen');
    const spinScreenPosition = `bottom-[${(window.innerHeight - (spinButton?.getBoundingClientRect().bottom || 656)
            - (spinButton?.getBoundingClientRect().height || 107) + 40).toFixed(0)}px]`;
    const multiplyPosition = `bottom-[${(window.innerHeight - (spinInfo?.getBoundingClientRect().bottom || 656)
        - (spinInfo?.getBoundingClientRect().height || 107) + 125).toFixed(0)}px]`;
    switch (screen) {
      case 1:
        return 'top-[0.6rem]';
      case 2:
      case 3:
      case 4:
      case 5:
      case 7:
      case 10:
      case 11:
      case 12:
      case 13:
      case 14:
        return 'top-[0.9rem]';
      case 6:
        return spinScreenPosition;
      case 8:
        return 'bottom-[-40px]';
      case 9:
        return multiplyPosition;
      default:
        return 'top-[0.9rem]';
    }
  };

  if (!open) return null;
  return (
    <div>
      {getScreenHighlights()?.customElement && getScreenHighlights()?.customElement}
      {createPortal(
        <div
          className="fixed top-0 h-screen w-screen dsadsa z-[2000] flex items-center justify-center bg-black bg-opacity-50"
          id="tutorial-backdrop"
        >
          <div className={`relative transition-all ${getModalPosition()} w-full px-4 text-center`}>
            <Suspense fallback={<div />}>
              {screens[screen - 1]}
            </Suspense>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default TutorialModal;

const CustomElement = () => {
  const icon = getBoosterIcon('brain-bot');
  return createPortal(
    <>
      <img src={BoosterStarBg} alt="star-bg" className="w-auto h-auto absolute top-[2.1rem] right-0 z-[2000]" />
      <div
        className="absolute top-[5.1rem] right-[0.7rem] p-1 rounded flex flex-col gap-[24px] max-h-[58vh] overflow-y-scroll z-[2000]"
        id="tutorial-booster"
      >
        <div className="flex flex-col items-center max-w-[60px] text-white">
          <img src={icon} alt="booster-icon" className="w-[45px] h-[45px]" />
          <div className="font-Bangers text-xs uppercase leading-[12.77px]">Booster</div>
          <div className="font-Barlow text-xs uppercase leading-[12px]">
            <div>
              <span>{56}</span>
              {' '}
              :
              <span>{32}</span>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
